import { SamlIntegrationSetupResponseDto, SsoConfigCreateDto, SsoConfigResponseDto, SsoConfigUpdateDto, } from '@lucidlink/dto/web-service-api';
import { arrayToBase64, cryptoKeyToArray } from '@lucidlink/ts-crypto/crypto';
import { parserFromDto } from '#fetcher';
export const createSsoManager = ({ workspaceFetcher, getAdminCrypto, getInvitationManager, loginToWorkspace, }) => {
    return new SsoManagerImpl(workspaceFetcher, getAdminCrypto, getInvitationManager, loginToWorkspace);
};
class SsoManagerImpl {
    workspaceFetcher;
    getAdminCrypto;
    getInvitationManager;
    loginToWorkspace;
    ssoFetcher;
    constructor(workspaceFetcher, getAdminCrypto, getInvitationManager, loginToWorkspace) {
        this.workspaceFetcher = workspaceFetcher;
        this.getAdminCrypto = getAdminCrypto;
        this.getInvitationManager = getInvitationManager;
        this.loginToWorkspace = loginToWorkspace;
        this.ssoFetcher = workspaceFetcher.createSubFetcher({
            path: ['sso-integration'],
        });
    }
    async getConfig() {
        await this.loginToWorkspace();
        await this.getAdminCrypto();
        return await this.ssoFetcher.get([], {
            parser: parserFromDto(SsoConfigResponseDto),
            queryParams: { domainCrypto: true },
        });
    }
    async getSamlIntegrationUrls() {
        await this.loginToWorkspace();
        await this.getAdminCrypto();
        const integrationSetup = await this.workspaceFetcher.get(['sso', 'saml', 'integration-setup'], {
            parser: parserFromDto(SamlIntegrationSetupResponseDto),
        });
        return {
            assertionConsumerUrl: new URL(integrationSetup.assertionConsumer),
            audienceUrl: new URL(integrationSetup.spEntityId),
        };
    }
    /**
     * @deprecated Not used in production. Only called by old SDK clients and e2e tests.
     * Superseded by {@link DomainManager.exportDomainSecret}.
     */
    async getSecret() {
        await this.loginToWorkspace();
        const adminCrypto = await this.getAdminCrypto();
        // domainCrypto param signals the server that this client is domain-crypto-aware.
        // Without it, the server blocks the request after m003 migration (UpgradeRequiredException).
        // Added here because e2e tests still call getSecret() via helpers when the
        // Old deployed SDK binaries won't have this param, so the server-side block still works.
        await this.ssoFetcher.get([], { queryParams: { domainCrypto: true } });
        const ssoCrypto = await adminCrypto.getSsoCrypto();
        const ssoSecretKey = await ssoCrypto.verifyAndGetSsoSecretKey();
        const ssoSecretKeyArray = await cryptoKeyToArray(ssoSecretKey);
        return arrayToBase64(ssoSecretKeyArray);
    }
    async createIntegration(idpEntityId, signInUrl, certificate) {
        await this.loginToWorkspace();
        await this.getAdminCrypto();
        await this.getInvitationManager().ensureSsoInvitation();
        const ssoConfigDto = SsoConfigCreateDto.create({
            idpEntityId,
            signInUrl,
            certificate,
        });
        await this.ssoFetcher.post([], {
            body: ssoConfigDto,
            parser: parserFromDto(SsoConfigResponseDto),
        });
    }
    async updateCertificate(certificate) {
        await this.loginToWorkspace();
        await this.getAdminCrypto();
        const updateDto = SsoConfigUpdateDto.create({ certificate });
        await this.ssoFetcher.patch([], {
            body: updateDto,
            parser: parserFromDto(SsoConfigResponseDto),
        });
    }
    async updateExternalUsersEnforcement(externalUsersEnforcement) {
        await this.loginToWorkspace();
        await this.getAdminCrypto();
        await this.ssoFetcher.patch([], {
            body: SsoConfigUpdateDto.create({
                externalUsersEnforcement,
            }),
            parser: parserFromDto(SsoConfigResponseDto),
        });
    }
    async removeIntegration() {
        await this.loginToWorkspace();
        await this.getAdminCrypto();
        await this.ssoFetcher.delete([]);
    }
    async rotateScimApiKey() {
        await this.ssoFetcher.put('/scim-api-key');
    }
}
